import { useCallback, useRef, useEffect } from 'react';
import { telemetry, SpanStatusCode } from '@/telemetry-browser';
import { trace } from '@opentelemetry/api';
import { frontendTraceManager } from '@/telemetry/FrontendTraceManager';

// Global tracker to prevent duplicate sessions across component instances
const globalSessionTracker = new Map<string, boolean>();

// Emergency circuit breaker to prevent session spam
const sessionCallTracker = new Map<string, { count: number, lastCall: number }>();
const MAX_SESSION_CALLS_PER_MINUTE = 3;
const CIRCUIT_BREAKER_TIMEOUT = 60000; // 1 minute

interface VideoCallSessionData {
  callId?: string;
  organizationId?: string;
  leadId?: string;
  assistantId?: string;
  isInterviewSession?: boolean;
  roomName?: string;
}

/**
 * Video call telemetry hook that works within the single parent trace architecture.
 * Creates child spans under the main frontend trace instead of separate traces.
 * Prevents duplicate spans and ensures proper cleanup.
 */
export function useVideoCallTelemetry() {
  // Debug: Track hook instance creation
  const hookInstanceId = useRef(Math.random().toString(36).substr(2, 9));
  
  useEffect(() => {
    console.log(`🎆 useVideoCallTelemetry hook instance created: ${hookInstanceId.current}`);
    return () => {
      console.log(`🎆 useVideoCallTelemetry hook instance destroyed: ${hookInstanceId.current}`);
    };
  }, []);
  
  // Track active session span to prevent duplicates
  const sessionSpanRef = useRef<any>(null);
  const sessionDataRef = useRef<VideoCallSessionData | null>(null);
  const isSessionActiveRef = useRef(false);

  const startVideoCallSession = useCallback((sessionData: VideoCallSessionData) => {
    const timestamp = new Date().toISOString();
    const now = Date.now();
    const callKey = `${sessionData.callId}-${sessionData.organizationId}`;
    
    // EMERGENCY CIRCUIT BREAKER - prevent session spam
    const callHistory = sessionCallTracker.get(callKey) || { count: 0, lastCall: 0 };
    
    // Reset counter if more than 1 minute has passed
    if (now - callHistory.lastCall > CIRCUIT_BREAKER_TIMEOUT) {
      callHistory.count = 0;
    }
    
    callHistory.count++;
    callHistory.lastCall = now;
    sessionCallTracker.set(callKey, callHistory);
    
    if (callHistory.count > MAX_SESSION_CALLS_PER_MINUTE) {
      console.error(`🚨 CIRCUIT BREAKER TRIGGERED: Too many session calls for ${callKey} (${callHistory.count} calls). Blocking to prevent spam.`);
      console.error(`🚨 This indicates a serious bug - session creation is being called repeatedly!`);
      return;
    }
    
    console.log(`🔍 [${timestamp}] startVideoCallSession called for ${sessionData.callId} (attempt ${callHistory.count})`);
    
    // Prevent duplicate session spans
    if (isSessionActiveRef.current && sessionSpanRef.current) {
      console.warn(`✅ Video call session already active for call ${sessionDataRef.current?.callId}, reusing existing span`);
      return;
    }

    // Additional check: prevent duplicate sessions for the same call ID
    if (sessionDataRef.current?.callId === sessionData.callId && isSessionActiveRef.current) {
      console.warn(`✅ Session already exists for call ID ${sessionData.callId}, reusing existing session`);
      return;
    }

    // Global check: prevent duplicate sessions across different component instances
    const globalKey = `${sessionData.callId}-${sessionData.organizationId}`;
    if (globalSessionTracker.has(globalKey)) {
      console.warn(`✅ Global session already active for ${globalKey}, reusing existing session`);
      return;
    }

    try {
      console.log(`🎥 Creating video call session as CHILD SPAN under main frontend trace for ${sessionData.callId}`);
      
      // Create child span for video call session under the main frontend trace
      const sessionSpan = frontendTraceManager.createChildSpan('video_call.session', {
        attributes: {
          'video_call.id': sessionData.callId || 'unknown',
          'video_call.organization_id': sessionData.organizationId || 'unknown',
          'video_call.lead_id': sessionData.leadId || 'unknown',
          'video_call.assistant_id': sessionData.assistantId || 'unknown',
          'video_call.is_interview': sessionData.isInterviewSession || false,
          'video_call.room_name': sessionData.roomName || 'unknown',
          'video_call.started_at': new Date().toISOString(),
          'video_call.client_type': 'web',
          'video_call.hook_instance': hookInstanceId.current,
        },
      });
      
      if (!sessionSpan) {
        console.error('❌ Failed to create video call session span - parent trace not ready');
        return;
      }

      sessionSpanRef.current = sessionSpan;
      sessionDataRef.current = sessionData;
      isSessionActiveRef.current = true;
      
      // Mark session as active globally
      globalSessionTracker.set(globalKey, true);

      // Add session start event to main parent trace
      frontendTraceManager.addEvent('video_call.session_started', {
        'video_call.id': sessionData.callId,
        'video_call.type': sessionData.isInterviewSession ? 'video_interview' : 'video_call',
        'video_call.organization_id': sessionData.organizationId,
        'video_call.lead_id': sessionData.leadId,
        'video_call.hook_instance': hookInstanceId.current,
      });

      console.log(`✅ Started video call session as child span: ${sessionData.callId} [Global: ${globalKey}]`);
    } catch (error) {
      console.error('❌ Failed to start video call session telemetry:', error);
    }
  }, []);

  const endVideoCallSession = useCallback((
    reason?: string,
    error?: Error,
    sessionMetrics?: {
      duration?: number;
      violationCount?: number;
      networkDisconnections?: number;
    }
  ) => {
    if (!isSessionActiveRef.current || !sessionSpanRef.current) {
      console.warn('No active video call session to end');
      return;
    }

    try {
      const sessionSpan = sessionSpanRef.current;
      
      // Add session end attributes
      sessionSpan.setAttributes({
        'video_call.ended_at': new Date().toISOString(),
        'video_call.end_reason': reason || 'unknown',
        'video_call.duration_seconds': sessionMetrics?.duration || 0,
        'video_call.violation_count': sessionMetrics?.violationCount || 0,
        'video_call.network_disconnections': sessionMetrics?.networkDisconnections || 0,
      });

      if (error) {
        // Properly capture error with full details
        sessionSpan.recordException(error);
        sessionSpan.setStatus({
          code: SpanStatusCode.ERROR,
          message: `Video call session failed: ${error.message}`,
        });
        // Add error details to span attributes for better visibility
        sessionSpan.setAttributes({
          'error.type': error.name || 'Error',
          'error.message': error.message,
          'error.stack': error.stack || 'No stack trace',
        });
        console.error(`🔴 Video call session ended with error: ${error.message}`, error);
      } else {
        sessionSpan.setStatus({ code: SpanStatusCode.OK });
        console.log(`✅ Video call session ended successfully: ${reason}`);
      }

      // End the session span properly
      sessionSpan.end();

      // Record session metric
      const metrics = telemetry.createCustomMetrics();
      metrics.callDurationHistogram.record(sessionMetrics?.duration || 0, {
        call_type: 'video_call',
        status: error ? 'error' : 'success',
        end_reason: reason || 'unknown',
        organization_id: sessionDataRef.current?.organizationId || 'unknown',
      });
    } catch (telemetryError) {
      console.error('Failed to end video call session telemetry:', telemetryError);
      // If we failed to end the session properly, at least record the error
      try {
        const errorSpan = telemetry.startSpan('video-call.session-end-error', {
          attributes: {
            'error.type': 'telemetry_failure',
            'error.message': telemetryError instanceof Error ? telemetryError.message : 'Unknown error',
            'video_call.id': sessionDataRef.current?.callId || 'unknown',
          },
        });
        errorSpan.recordException(telemetryError as Error);
        errorSpan.setStatus({ code: SpanStatusCode.ERROR });
        errorSpan.end();
      } catch (nestedError) {
        console.error('Failed to record telemetry error:', nestedError);
      }
    } finally {
      // Always reset state
      const callId = sessionDataRef.current?.callId;
      const organizationId = sessionDataRef.current?.organizationId;
      
      sessionSpanRef.current = null;
      sessionDataRef.current = null;
      isSessionActiveRef.current = false;
      
      // Clear global session tracker
      if (callId && organizationId) {
        const globalKey = `${callId}-${organizationId}`;
        globalSessionTracker.delete(globalKey);
        console.log(`🧹 Cleared global session tracker for ${globalKey}`);
      }
    }
  }, []);

  const recordViolation = useCallback((
    violationType: string,
    severity: 'low' | 'medium' | 'high' | 'critical',
    message: string,
    additionalData?: Record<string, any>
  ) => {
    console.log(`🚨 [VideoCallTelemetry] recordViolation called:`, {
      violationType,
      severity,
      message,
      additionalData,
      sessionActive: isSessionActiveRef.current,
      sessionId: sessionDataRef.current?.callId
    });
    
    try {
      // Add violation event to the main parent trace instead of creating separate spans
      frontendTraceManager.addEvent(`video_call.violation.${violationType}`, {
        'video_call.violation.type': violationType,
        'video_call.violation.severity': severity,
        'video_call.violation.message': message,
        'video_call.violation.timestamp': new Date().toISOString(),
        'video_call.session.active': isSessionActiveRef.current,
        'video_call.session_id': sessionDataRef.current?.callId || 'unknown',
        'video_call.hook_instance': hookInstanceId.current,
        ...Object.entries(additionalData || {}).reduce((acc, [key, value]) => {
          acc[`video_call.violation.${key}`] = String(value);
          return acc;
        }, {} as Record<string, string>),
      });

      // Record violation metric
      const metrics = telemetry.createCustomMetrics();
      metrics.violationCounter.add(1, {
        type: violationType,
        severity,
        session_active: isSessionActiveRef.current.toString(),
        call_id: sessionDataRef.current?.callId || 'unknown',
      });

      console.log(`⚠️ Recorded violation event: ${violationType} (${severity}) - ${message}`);
    } catch (error) {
      console.error('❌ Failed to record violation:', error);
    }
  }, []);

  const recordNetworkEvent = useCallback((
    eventType: 'connected' | 'disconnected' | 'reconnected',
    reason?: string,
    metadata?: Record<string, any>
  ) => {
    try {
      // Add network event to main parent trace instead of creating separate spans
      frontendTraceManager.addEvent(`video_call.network.${eventType}`, {
        'video_call.network.event': eventType,
        'video_call.network.reason': reason || 'unknown',
        'video_call.network.timestamp': new Date().toISOString(),
        'video_call.session_id': sessionDataRef.current?.callId || 'unknown',
        'video_call.hook_instance': hookInstanceId.current,
        ...Object.entries(metadata || {}).reduce((acc, [key, value]) => {
          acc[`video_call.network.${key}`] = String(value);
          return acc;
        }, {} as Record<string, string>),
      });

      console.log(`🔌 Recorded network event: ${eventType} - ${reason}`);
    } catch (error) {
      console.error('❌ Failed to record network event:', error);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isSessionActiveRef.current && sessionSpanRef.current) {
        endVideoCallSession('component_unmount');
      }
    };
  }, [endVideoCallSession]);

  return {
    startVideoCallSession,
    endVideoCallSession,
    recordViolation,
    recordNetworkEvent,
    isSessionActive: () => isSessionActiveRef.current,
    getSessionData: () => sessionDataRef.current,
  };
}